<template>
    <BannerPreview
        :id="bannerId"
        :key="refreshKey"
        :preview-values="livePreviewValues"
        :editor-values="liveEditorValues"
        :ad-type="masterTemplate.adType"
        :meta-preview-size="metaPreviewSize"
        content-clickable
        debug
        lazy-load
        :scaling-factor="scalingFactor"
        :playback-namespace="playbackNamespace"
        @bannerLoaded="onBannerLoaded"
    />
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { CSSSettings, MasterTemplate, Editable, EditableWithOverwrites, EditableTypeType } from "hoxton-types";

import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { EditableType, EditableMediaTypes, LegacyEditableTypes } from "hoxton-shared/enums/editables";
import nl2br from "hoxton-shared/utils/nl2br";
import unique from "hoxton-shared/utils/unique";

import type { Asset } from "@/types/types";

import type { ComputedOverwrite } from "@/types";
import bus from "@/bus";

import BannerPreview from "@/components/Previews/BannerPreview.vue";

import { CampaignGetters } from "@/store/modules/campaign";
import { useEditableMethods } from "@/composables/useEditableMethods";

import { EditableTranslateMethod } from "@/enums/editables";
import { BannerEvents } from "@/enums/banners";
import { MediaLibraryAssetEvent } from "@/enums/mediaLibrary";

import { debounce, getObjectValue, kebabCase } from "@/utils";
import { cleanAWSTokens } from "@/utils/urls";
import { getBannerIdFromMasterTemplate } from "@/utils/getBannerIframeId";

interface IframeUpdate {
    name: string;
    type: string;
    value: string;
}

interface StylePosition {
    top?: string;
    left?: string;
    position?: string;
    transform?: string;
    marginTop?: string;
    marginLeft?: string;
    backgroundPositionY?: string;
    backgroundPositionX?: string;
}

interface Style {
    visibility?: string;
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    fontStyle?: string;
    letterSpacing?: string;
    lineHeight?: string;
    height?: string;
    width?: string;
    transform?: string;
    fontFamily?: string;
    position?: StylePosition;
}

function setDivTextValue(element: HTMLDivElement, value: string, editable: Editable) {
    if (editable.type === EditableType.Image) {
        const elementStyles = window.getComputedStyle(element);

        if (elementStyles.backgroundImage !== "none") {
            element.style.backgroundImage = `url(${value})`;
            return;
        }
    }

    if (editable.type === LegacyEditableTypes.BackgroundImage) {
        element.style.backgroundImage = `url(${value})`;
        return;
    }

    if (editable.type === EditableType.Textarea) {
        element.innerHTML = nl2br(value);
        return;
    }

    // a hack to make image wrapped with a div to update correctly upon asset selection change
    if (getObjectValue(element, "firstChild.nodeName") === "IMG") {
        (element.children[0] as HTMLImageElement).src = value;
    } else {
        element.innerHTML = value;
    }
}

function resetTransformParam(el: HTMLElement) {
    const currentTransforms = el.style.transform;
    const transformWithoutTranslate = currentTransforms
        ? currentTransforms.replace(/translate\([^)]*\)(;|)?/, "")
        : currentTransforms;

    const transform = transformWithoutTranslate
        ? transformWithoutTranslate.replace(/scale\([^)]*\)(;|)?/, "")
        : transformWithoutTranslate;

    if (transform) {
        el.style.setProperty("transform", transform);
    } else {
        el.style.removeProperty("transform");
    }
}

function getPositionTranslation(
    settings: CSSSettings,
    editableType: EditableTypeType,
    currentTransforms: string | undefined
) {
    const { translationMethod, top, left } = settings;
    const position = {} as StylePosition;
    const validCssNumericValue = (val?: null | number | string) => val !== undefined && val !== null;

    let method = translationMethod === undefined ? EditableTranslateMethod.Relative : translationMethod;

    if (editableType === LegacyEditableTypes.BackgroundImage) {
        method = EditableTranslateMethod.BackgroundPosition;
    }

    if (method === EditableTranslateMethod.Relative) {
        if (validCssNumericValue(top)) {
            position.top = `${top}px`;
            position.position = "relative";
        }

        if (validCssNumericValue(left)) {
            position.left = `${left}px`;
            position.position = "relative";
        }
    } else if (method === EditableTranslateMethod.Transform) {
        const y = validCssNumericValue(top) ? top : 0;
        const x = validCssNumericValue(left) ? left : 0;

        const translateCss = `translate(${x}px, ${y}px)`;

        if (!currentTransforms) {
            position.transform = translateCss;
        } else {
            let transform = currentTransforms.replace(/translate\([^)]*\)(;|)?/, "");
            transform = transform ? `${transform} ${translateCss}` : `${translateCss}`;

            position.transform = transform;
        }
    } else if (method === EditableTranslateMethod.Margin) {
        if (validCssNumericValue(top)) {
            position.marginTop = `${top}px`;
        }
        if (validCssNumericValue(left)) {
            position.marginLeft = `${left}px`;
        }
    }

    if (method === EditableTranslateMethod.BackgroundPosition) {
        if (validCssNumericValue(top)) {
            position.backgroundPositionY = `${top}px`;
        }
        if (validCssNumericValue(left)) {
            position.backgroundPositionX = `${left}px`;
        }
    }

    return position;
}

interface Changes {
    editable: Editable;
    value: string;
    settings: CSSSettings;
}

const LiveBannerPreview = defineComponent({
    name: "LiveBannerPreview",
    components: { BannerPreview },
    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        },

        previewValues: {
            type: String,
            required: true
        },

        scalingFactor: {
            type: Number,
            default: 0
        },

        playbackNamespace: {
            type: String,
            required: true
        },

        metaPreviewSize: {
            type: Object as PropType<{ width?: number; height?: number; maxHeight?: number; minHeight?: number }>,
            required: false
        }
    },

    emits: [BannerEvents.BannerLoaded],

    setup() {
        const { getEditablePreviewValue, getAffectingGroups, getEditableSettings } = useEditableMethods();

        return {
            getEditablePreviewValue,
            getEditableSettings,
            getAffectingGroups
        };
    },

    data() {
        return {
            appliedValues: {} as Record<string, Changes & { domElementExists?: boolean }>,
            stagedChanges: {} as Record<string, Changes>,
            removedAsset: null as null | Asset,
            refreshKey: 0,
            livePreviewValues: "",
            liveUpdates: {} as Record<string, Changes>,
            addedFonts: [] as string[],
            waitForElementCounter: 0,
            liveEditorValues: {},

            // A debounced function needs to be unique for every instance of the component
            // and as such should be defined as a data property not a method as methods are shared
            reloadPsdTemplate: debounce(function handler(this: typeof LiveBannerPreview) {
                this.livePreviewValues = this.previewValues;
                this.refreshKey += 1;
            }, 500),

            unsubscribeOverwritesWatcher: null as null | (() => void),
            unsubscribeRemovedOverwritesWatcher: null as null | (() => void),
            unsubscribeSavedOverwritesWatcher: null as null | (() => void),
            unsubscribeLanguageWatcher: null as null | (() => void),
            unsubscribeGroupValuesWatcher: null as null | (() => void)
        };
    },

    computed: {
        bannerId() {
            return getBannerIdFromMasterTemplate(this.masterTemplate, this.playbackNamespace);
        },

        editablesWithLocalOverwrites() {
            const localChanges = this.$store.state.editor.localEditableOverwrites.reduce((acc, editableOverwrite) => {
                const groups = this.getAffectingGroups(editableOverwrite.editable, this.masterTemplate._id) as string[];

                const relevantGVIds = groups.reduce((accumulator, groupId) => {
                    return accumulator.concat(
                        this.$store.state.campaign.normalized.editableGroups[groupId].editableGroupValues
                    );
                }, [] as string[]);

                const relevantOverwrites = editableOverwrite.overwrites.filter(overwrite =>
                    overwrite.editableGroupValueIds.some(egvId => relevantGVIds.includes(egvId))
                );

                if (relevantOverwrites.length) {
                    acc.push(editableOverwrite.editable._id);
                }

                return acc;
            }, [] as string[]);

            return this.templateEditables.filter(editable => localChanges.includes(editable._id));
        },

        normalizedTemplateEditables() {
            return this.templateEditables.reduce(
                (acc, editable) => Object.assign(acc, { [editable._id]: editable }),
                {} as Record<string, Editable>
            );
        },

        selectedLanguage() {
            return this.$store.state.editor.selectedLanguage;
        },

        templateEditables() {
            return this.$store.getters[CampaignGetters.templateEditables](this.masterTemplate._id) as Editable[];
        }
    },

    watch: {
        masterTemplate(value, oldValue) {
            if (value.preview !== oldValue.preview) {
                this.livePreviewValues = this.previewValues;
            }

            if (value.modified !== oldValue.modified) {
                this.refreshKey += 1;
            }
        },

        templateEditables() {
            this.updateDomTemplateEditables();
        }
    },

    created() {
        this.initAppliedValues();
        this.livePreviewValues = this.previewValues;

        this.unsubscribeOverwritesWatcher = this.$store.watch(
            state => state.editor.localEditableOverwrites,
            this.watchOverwrites.bind(this),
            { deep: true }
        );

        this.unsubscribeRemovedOverwritesWatcher = this.$store.watch(
            state => state.editor.removedOverwriteIds,
            this.watchRemovedOverwrites.bind(this),
            { deep: true }
        );

        this.unsubscribeSavedOverwritesWatcher = this.$store.watch(
            state => state.campaign.normalized.overwrites,
            this.watchSavedOverwrites.bind(this),
            { deep: true }
        );

        this.unsubscribeLanguageWatcher = this.$store.watch(
            state => state.editor.selectedLanguage,
            this.watchLanguage.bind(this),
            { deep: true }
        );

        this.unsubscribeGroupValuesWatcher = this.$store.watch(
            state => state.editor.selectedGroupValueIds,
            this.watchGroupValues.bind(this),
            { deep: true }
        );

        bus.$on(MediaLibraryAssetEvent.AssetRemoved, this.onAssetRemoved);
        // This code is meant to be used in sandbox mode to reapply values on hot reload
        // It can be called from within the banner template using postMessage after the banner is initiated
        //  and manifest values are set
        window.addEventListener("message", this.onBannerContentLoaded, false);
    },

    beforeUnmount() {
        this.unsubscribeGroupValuesWatcher?.();
        this.unsubscribeLanguageWatcher?.();
        this.unsubscribeOverwritesWatcher?.();
        this.unsubscribeRemovedOverwritesWatcher?.();
        this.unsubscribeSavedOverwritesWatcher?.();

        bus.$off(MediaLibraryAssetEvent.AssetRemoved, this.onAssetRemoved);

        window.removeEventListener("message", this.onBannerContentLoaded, false);
    },

    methods: {
        getIframeElement() {
            return this.$el.querySelector(`#${this.bannerId}`) as HTMLIFrameElement;
        },

        getAppliedValuesKey(editable: Editable) {
            const groupsAffectingEditable =
                this.normalizedTemplateEditables[editable._id].defaultValues[0].editableGroups;

            return `${editable._id}-${groupsAffectingEditable!.sort().join()}`;
        },

        getLiveEditableValue(editable: Editable, groupsAffectingEditable: string[], masterTemplate: MasterTemplate) {
            // The preview function gets an image thumbnail for folder types and converts nl2br for textareas
            let value = this.getEditablePreviewValue(editable, groupsAffectingEditable, masterTemplate);

            if (editable.type === EditableType.Folder) {
                value += "index.html";
            }

            return value as string;
        },

        getLiveEditableSettings(editable: Editable, groupsAffectingEditable: string[], masterTemplateId: string) {
            // This function internally handles the merging of overwrite levels
            return this.getEditableSettings(editable, groupsAffectingEditable, masterTemplateId);
        },

        initAppliedValues() {
            this.templateEditables.forEach(editable => {
                const groupsAffectingEditable = editable.defaultValues[0].editableGroups as string[];
                const value = this.getLiveEditableValue(editable, groupsAffectingEditable, this.masterTemplate);
                const settings = this.getLiveEditableSettings(
                    editable,
                    groupsAffectingEditable,
                    this.masterTemplate._id as string
                );

                this.appliedValues[this.getAppliedValuesKey(editable)] = {
                    editable,
                    value,
                    settings
                };
            });
        },

        onAssetRemoved(asset: Asset) {
            this.removedAsset = asset;
        },

        onBannerContentLoaded(evt: MessageEvent<string>) {
            if (evt.data === "contentLoaded") {
                this.reapplyValues();
            }
        },

        onBannerLoaded() {
            const stagedKeys = Object.keys(this.stagedChanges);
            if (stagedKeys.length) {
                stagedKeys.forEach(stagedEditableId =>
                    this.updateDom(
                        this.stagedChanges[stagedEditableId].editable,
                        this.stagedChanges[stagedEditableId].value,
                        this.stagedChanges[stagedEditableId].settings
                    )
                );

                this.stagedChanges = {};
            }

            this.doReapplyValues();
            // force banner to reapply the live styles on first load ( scenario where we go from editor to library and back to the editor and we have selected group values)
            if (
                this.masterTemplate.adType === TemplateType.HTML5 &&
                this.$store.state.editor.selectedGroupValueIds.length
            ) {
                this.templateEditables.forEach(editable => {
                    const groupsAffectingEditable = editable.defaultValues[0].editableGroups as string[];
                    const settings = this.getLiveEditableSettings(
                        editable,
                        groupsAffectingEditable,
                        this.masterTemplate._id as string
                    );

                    this.updateHtmlStyles(this.normalizedTemplateEditables[editable._id], settings);
                });
            }

            this.$emit(BannerEvents.BannerLoaded);
        },

        reapplyValues() {
            this.waitForElementCounter = 0;
            const appliedValuesKeys = Object.keys(this.liveUpdates);
            const editableIdThatShouldExist = appliedValuesKeys.filter(avk => {
                return this.appliedValues[avk].domElementExists;
            });

            if (editableIdThatShouldExist.length) {
                this.waitForElement(this.liveUpdates[editableIdThatShouldExist[0]].editable.name);
            } else if (appliedValuesKeys.length) {
                this.doReapplyValues();
            }
        },

        updateDomTemplateEditables() {
            this.templateEditables.forEach(editable => {
                const groupsAffectingEditable = editable.defaultValues[0].editableGroups as string[];
                this.updateEditableDomValue(this.normalizedTemplateEditables[editable._id], groupsAffectingEditable);
            });
        },

        waitForElement(editableName: string) {
            this.waitForElementCounter += 1;
            const iframe = this.getIframeElement();
            const selector = `#${editableName}`;

            if (!iframe) {
                console.log("iframe not found", this.bannerId);

                return;
            }

            if (iframe.contentDocument?.querySelectorAll(selector).length) {
                this.doReapplyValues();
            } else if (this.waitForElementCounter < 10) {
                setTimeout(() => this.waitForElement(editableName), 1000);
            }
        },

        doReapplyValues() {
            const appliedValuesKeys = Object.keys(this.liveUpdates);

            if (appliedValuesKeys.length) {
                appliedValuesKeys.forEach(stagedEditableId => {
                    this.updateDom(
                        this.liveUpdates[stagedEditableId].editable,
                        this.liveUpdates[stagedEditableId].value,
                        this.liveUpdates[stagedEditableId].settings
                    );
                });
            }
        },

        shouldUpdateSettings(settings: CSSSettings, editable: Editable) {
            const appliedValueKey = this.getAppliedValuesKey(editable);

            return (
                settings &&
                this.appliedValues[appliedValueKey]?.settings &&
                JSON.stringify(this.appliedValues[appliedValueKey].settings) !== JSON.stringify(settings)
            );
        },

        triggerBannerUpdate(update: IframeUpdate) {
            this.callBannerMethod("hoxton.setState", update);
            this.callBannerMethod("Creative.updateContent", update);
        },

        /**
         * @param {string} method the name of the method to call on the iframe contentWindow. Can specify dot notation e.g. hoxton.setState
         * @param {{name: string, type:string, value: any}} update the update to pass into the method
         */
        callBannerMethod(method: string, update: IframeUpdate) {
            const iframe = this.getIframeElement();

            if (!iframe) {
                console.log("iframe not found", this.bannerId);

                return;
            }

            const callback = getObjectValue(iframe, `contentWindow.${method}`);
            if (callback && typeof callback === "function") {
                callback(update);
            }
        },

        updateDomSettings(node: HTMLElement, settings: CSSSettings, iframe: Document, editable: Editable) {
            const appliedStyles = node.dataset.appliedSettings;
            const appliedStylesList = appliedStyles ? appliedStyles.split(",") : [];

            const style = {} as Style;

            style.visibility = settings.visible === false ? "hidden" : "visible";

            if (settings.fontColor) {
                style.color = settings.fontColor;
            }

            if (settings.fontFamily !== undefined) {
                if (!this.addedFonts.includes(settings.fontFamily.name)) {
                    this.addedFonts.push(settings.fontFamily.name);

                    const head = iframe.head || iframe.getElementsByTagName("head")[0];
                    const fontFaceStyle = iframe.createElement("style");
                    const cssText = `@font-face {
                        font-family: '${settings.fontFamily.name}';
                        src: url('${settings.fontFamily.url}');
                    }`;

                    head.appendChild(fontFaceStyle);
                    fontFaceStyle.type = "text/css";
                    fontFaceStyle.appendChild(iframe.createTextNode(cssText));
                }

                style.fontFamily = `"${settings.fontFamily.name}"`;
            }

            if (settings.fontSize !== undefined) {
                style.fontSize = `${settings.fontSize}px`;
            }

            if (settings.fontWeight !== undefined) {
                style.fontWeight = settings.fontWeight;
            }

            if (settings.fontStyle !== undefined) {
                style.fontStyle = settings.fontStyle;
            }

            if (settings.letterSpacing !== undefined) {
                style.letterSpacing = `${settings.letterSpacing}px`;
            }

            if (settings.lineHeight !== undefined) {
                style.lineHeight = `${settings.lineHeight}px`;
            }

            if (settings.height !== undefined) {
                style.height = `${settings.height}px`;
            }

            if (settings.width !== undefined) {
                style.width = `${settings.width}px`;
            }

            if (settings.scale !== undefined) {
                const currentTransforms = style.transform;
                const scaleCss = `scale(${settings.scale / 100})`;

                if (!currentTransforms) {
                    style.transform = `${scaleCss}`;
                } else {
                    let transform = currentTransforms.replace(/scale\([^)]*\)(;|)?/, "");
                    transform = transform ? `${transform} ${scaleCss}` : `${scaleCss}`;

                    style.transform = transform;
                }
            }

            const position = getPositionTranslation(settings, editable.type, style.transform);

            if (Object.keys(position).length) {
                Object.assign(style, position);
            }

            resetTransformParam(node);

            const propsToUpdate = Object.keys(style);

            if (appliedStyles) {
                appliedStylesList.forEach(styleProp => {
                    if (!propsToUpdate.includes(styleProp)) {
                        node.style.removeProperty(kebabCase(styleProp));
                    }
                });
            }

            Object.assign(node.style, style);
            node.dataset.appliedSettings = propsToUpdate.join(",");
        },

        updatePsd(editable: Editable, value: string, settings: CSSSettings) {
            if (editable.layers) {
                const values = JSON.parse(this.previewValues);
                const layer = editable.layers.find(
                    ({ masterTemplateId }) => masterTemplateId === this.masterTemplate._id
                );

                if (layer) {
                    const layerInTemplate = (values.computedOverwrites as ComputedOverwrite[]).find(
                        overwrite => overwrite?.layer?.id === layer?.value?.id
                    );

                    if (layerInTemplate) {
                        this.reloadPsdTemplate();
                    }
                }
            }

            this.appliedValues[this.getAppliedValuesKey(editable)] = { editable, value, settings };
        },

        updateAE(editable: Editable, value: string, settings: CSSSettings) {
            if (editable.layers?.find(({ masterTemplateId }) => masterTemplateId === this.masterTemplate._id)) {
                this.liveEditorValues = {
                    ...(this?.liveEditorValues ? this.liveEditorValues : {}),
                    [editable.name]: { value, settings }
                };
            }

            this.appliedValues[this.getAppliedValuesKey(editable)] = { editable, value, settings };
        },

        updateHtml(editable: Editable, value: string, settings: CSSSettings) {
            let domElementExists = false;

            // Update dom for html banners
            const iframe = this.getIframeElement();
            const selector = `#${editable.name}`;

            if (!iframe) {
                console.log("iframe not found", this.bannerId);

                return;
            }

            if (iframe.contentDocument === null) {
                this.stagedChanges[editable._id] = {
                    editable,
                    value,
                    settings
                };

                return;
            }

            const templateId = this.bannerId.replace("iframe-", "");
            const defaultValue = editable.defaultValues.find(dv => dv.masterTemplateId === templateId);
            // Make sure the iframes folder editables are loaded from the same domain as the client when not using the defaultValue
            // This ensures the iframes are loaded from the correct server in sandbox mode
            const iframeFolderValue = (elem: HTMLIFrameElement) => {
                if (defaultValue && (defaultValue.value === value || `${defaultValue.value}index.html` === value)) {
                    elem.src = value;
                    return;
                }

                elem.src = `${window.location.origin}${value}`;
            };

            iframe.contentDocument.querySelectorAll(selector).forEach(e => {
                switch (e.nodeName) {
                    case "IFRAME":
                        if (editable.type === EditableType.Folder) {
                            iframeFolderValue(e as HTMLIFrameElement);
                            break;
                        }

                        (e as HTMLIFrameElement).src = value;
                        break;

                    case "IMG":
                        (e as HTMLImageElement).src = value;
                        break;

                    case "SOURCE":
                    case "VIDEO":
                        (e as HTMLVideoElement).src = value;

                        try {
                            if (iframe.contentDocument !== null) {
                                iframe.contentDocument.querySelector("video")?.load();
                            }
                        } catch (err) {
                            console.error("Video load error", err);
                        }
                        break;

                    case "DIV":
                        setDivTextValue(e as HTMLDivElement, value, editable);
                        break;

                    default:
                        if (editable.type === EditableType.Textarea) {
                            e.innerHTML = nl2br(value);
                        } else {
                            e.innerHTML = value;
                        }
                }

                if (this.shouldUpdateSettings(settings, editable) && iframe.contentDocument !== null) {
                    this.updateDomSettings(e as HTMLElement, settings, iframe.contentDocument, editable);
                }

                domElementExists = true;
            });

            const update = {
                name: editable.name,
                type: editable.type,
                value
            };

            this.triggerBannerUpdate(update);

            // Make sure we do not have undefined for the domElementExists check
            const appliedValueKey = this.getAppliedValuesKey(editable);
            const appliedValue = this.appliedValues[appliedValueKey] || {};

            this.appliedValues[appliedValueKey] = {
                editable,
                value,
                settings,
                domElementExists:
                    "domElementExists" in appliedValue
                        ? appliedValue.domElementExists || domElementExists
                        : domElementExists
            };
        },

        updateHtmlStyles(editable: Editable, settings: CSSSettings) {
            const iframe = this.getIframeElement();
            const selector = `#${editable.name}`;

            if (!iframe) {
                console.log("iframe not found", this.bannerId);

                return;
            }

            iframe.contentDocument?.querySelectorAll(selector).forEach(e => {
                const settingsProps = Object.keys(settings);

                if (settingsProps.length) {
                    this.updateDomSettings(e as HTMLElement, settings, iframe.contentDocument!, editable);
                }
            });
        },

        updateDom(editable: Editable, value: string, settings: CSSSettings) {
            // PSD banners are just images so need a refresh
            if (this.masterTemplate.adType === TemplateType.PSD) {
                this.updatePsd(editable, value, settings);
            } else if (this.masterTemplate.adType === TemplateType.AE) {
                this.updateAE(editable, value, settings);
            } else {
                this.updateHtml(editable, value, settings);
            }
        },

        updateLiveDom(editable: Editable, value: string, settings: CSSSettings) {
            this.liveUpdates[this.getAppliedValuesKey(editable)] = {
                editable,
                value,
                settings
            };

            this.updateDom(editable, value, settings);
        },

        updateEditableDomValue(editable: Editable, groupsAffectingEditable: string[]) {
            const editableValue = this.getLiveEditableValue(editable, groupsAffectingEditable, this.masterTemplate);
            const settings = this.getLiveEditableSettings(
                editable,
                groupsAffectingEditable,
                this.masterTemplate._id as string
            );
            const appliedValuesKey = this.getAppliedValuesKey(editable);

            const shouldUpdateDom = () => {
                // Not yet applied
                if (!(appliedValuesKey in this.appliedValues) || !("value" in this.appliedValues[appliedValuesKey])) {
                    return true;
                }

                // Settings change
                if (this.shouldUpdateSettings(settings, editable)) {
                    return true;
                }

                // Media type
                if (EditableMediaTypes.includes(editable.type as (typeof EditableMediaTypes)[number])) {
                    const cleanOldVal = cleanAWSTokens(this.appliedValues[appliedValuesKey].value);
                    const cleanNewVal = cleanAWSTokens(editableValue);
                    return cleanNewVal !== cleanOldVal;
                }

                // Array type
                if (Array.isArray(this.appliedValues[appliedValuesKey].value)) {
                    return JSON.stringify(this.appliedValues[appliedValuesKey].value) !== JSON.stringify(editableValue);
                }

                // Other values
                return this.appliedValues[appliedValuesKey].value !== editableValue;
            };

            if (shouldUpdateDom()) {
                this.updateLiveDom(editable, editableValue, settings);
            }
        },

        watchGroupValues() {
            this.updateDomTemplateEditables();
        },

        watchLanguage() {
            delete this.appliedValues[this.selectedLanguage];

            // lets reapply all the dom changes on language change
            this.updateDomTemplateEditables();

            // Run update content
            const update = {
                name: "language",
                type: "metadata",
                value: this.selectedLanguage
            };

            this.triggerBannerUpdate(update);
        },

        watchSavedOverwrites() {
            this.livePreviewValues = this.previewValues;

            if (this.removedAsset) {
                this.watchGroupValues();
                this.removedAsset = null;
            }
        },

        watchRemovedOverwrites(currentRemovedOverwriteIds: string[], previousRemovedOverwriteIds: string[]) {
            const removedOverwriteIds = unique(currentRemovedOverwriteIds.concat(previousRemovedOverwriteIds));
            const affectedOverwrites = this.$store.state.campaign.normalized.overwrites.filter(overwrite =>
                removedOverwriteIds.includes(overwrite._id)
            );

            affectedOverwrites.forEach(overwrite => {
                const editable = this.normalizedTemplateEditables[overwrite.editableId];
                const groupsAffectingEditable = editable.defaultValues[0].editableGroups as string[];
                this.updateEditableDomValue(editable, groupsAffectingEditable);
            });
        },

        watchOverwrites(_: EditableWithOverwrites[], previousEditablesWithLocalOverwrites: EditableWithOverwrites[]) {
            // Apply current overwrites to the banner
            const editableIdsWithLocalOverwrites = this.editablesWithLocalOverwrites.map(editable => {
                const groupsAffectingEditable = editable.defaultValues[0].editableGroups as string[];
                this.updateEditableDomValue(editable, groupsAffectingEditable);

                return editable._id;
            });

            // If overwrites were removed then we need to reapply the original values
            previousEditablesWithLocalOverwrites.forEach(editableOverwrite => {
                // These ones have already been applied in the loop above
                if (editableIdsWithLocalOverwrites.includes(editableOverwrite.editable._id)) {
                    return;
                }

                const groups = this.getAffectingGroups(editableOverwrite.editable, this.masterTemplate._id as string);
                const relevantGVIds = groups.reduce((acc, curr) => {
                    return acc.concat(this.$store.state.campaign.normalized.editableGroups[curr].editableGroupValues);
                }, []);

                const relevantOverwrites = editableOverwrite.overwrites.filter(overwrite =>
                    overwrite.editableGroupValueIds.some(egvId => relevantGVIds.includes(egvId))
                );
                if (relevantOverwrites.length) {
                    this.updateEditableDomValue(editableOverwrite.editable, groups);
                }
            });
        }
    }
});

export default LiveBannerPreview;
</script>
